(() => {
  const node = document.querySelector('[data-drv-native-gift-settings]');
  if (!node) return;
  const config = JSON.parse(node.textContent);
  if (!config.enabled || config.mode !== 'native-discount' || config.designMode) return;
  const logic = window.DrvDiscountGiftLogic;
  const root = window.Shopify.routes.root;
  const errorNode = document.querySelector('[data-drv-native-gift-error]');
  const errorText = errorNode?.querySelector('[data-drv-discount-gift-message]');
  const retryButton = errorNode?.querySelector('[data-drv-discount-gift-retry]');
  let chain = Promise.resolve(), busy = 0, checkoutPending = false;
  const savedDisabled = new Map(), allowedForms = new WeakSet();
  const checkoutControls = '[name="checkout"],[data-drv-direct-checkout]';
  function protect() {
    document.querySelectorAll(checkoutControls).forEach(button => {
      if (busy) {
        if (!savedDisabled.has(button)) savedDisabled.set(button, button.disabled);
        button.disabled = true;
      } else if (savedDisabled.has(button)) {
        button.disabled = savedDisabled.get(button);
        savedDisabled.delete(button);
      }
    });
  }
  function isSetupError(error) {
    return ['configuration','unavailable'].includes(error?.code || error?.message);
  }
  function customerMessage(error) {
    return isSetupError(error) ? (config.setupText || config.errorText) : config.errorText;
  }
  function message(error) {
    if (errorNode) errorNode.hidden = !error;
    if (errorText) errorText.textContent = customerMessage(error);
    if (retryButton) retryButton.hidden = isSetupError(error);
  }
  async function request(path, body, html = false) {
    const controller = new AbortController(), timer = setTimeout(()=>controller.abort(),15000);
    try {
      const response = await fetch(new URL(path, location.origin + root).href, body === undefined
        ? {cache:'no-store', signal:controller.signal}
        : {method:'POST', headers:{'Content-Type':'application/json'}, body:JSON.stringify(body), signal:controller.signal});
      if (!response.ok) throw new Error('network');
      const data = html ? await response.text() : await response.json();
      if (!html && (data.status || data.errors)) throw new Error('network');
      return data;
    } finally { clearTimeout(timer); }
  }
  function record(value) {
    if (value && typeof value === 'object' && !Array.isArray(value)) return value;
    try {
      for (let i=0;i<2;i++) value = String(value || '').replace(/&amp;/g,'&').replace(/&quot;|&#34;|&#x22;/gi,'"');
      const parsed = JSON.parse(value || '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch { return {}; }
  }
  function parse(html, selector) {
    const data = new DOMParser().parseFromString(html,'text/html').querySelector(selector);
    if (!data) throw new Error('configuration');
    return JSON.parse(data.textContent);
  }
  async function state() {
    const snapshot = parse(await request('cart?section_id=drv-gift-cart-state',undefined,true),'[data-drv-cart-state]');
    if (snapshot.enabled !== true || snapshot.mode !== 'native-discount') throw new Error('configuration');
    snapshot.giftWaivers = record(snapshot.discountGiftWaivers);
    for (const line of snapshot.items) if (Array.isArray(line.properties)) line.properties = Object.fromEntries(line.properties);
    return snapshot;
  }
  async function freshRule(productInfo) {
    const url = new URL(productInfo.getAttribute('data-product-url'),location.origin);
    if (url.origin !== location.origin) throw new Error('configuration');
    if (!url.pathname.startsWith(root)) url.pathname = root.replace(/\/$/,'') + url.pathname;
    url.searchParams.set('section_id','drv-gift-product-state');
    const rule = parse(await request(url.href,undefined,true),'[data-drv-current-gift-rule]');
    if (String(rule.productId) !== productInfo.getAttribute('data-product-id')) throw new Error('configuration');
    return logic.validateRule(rule);
  }
  function run(action, inline = false) {
    busy++; protect();
    const execute = ()=>window.navigator?.locks ? window.navigator.locks.request('drv-gift-cart',action) : action();
    const result = chain.then(execute).then(value=>{message(false);return value;}).catch(error=>{
      message(inline ? false : error);throw error;
    }).finally(()=>{busy--;protect();});
    chain = result.catch(()=>{});
    return result;
  }
  async function reconcile() {
    const before = await state(), waivers = logic.clampWaivers(before);
    if (JSON.stringify(waivers) !== JSON.stringify(before.giftWaivers)) {
      await request('cart/update.js',{attributes:{drv_discount_gift_waivers:JSON.stringify(waivers)}});
      before.giftWaivers = waivers;
    }
    const changes = logic.plan(before);
    if (Object.keys(changes.updates).length) await request('cart/update.js',{updates:changes.updates});
    if (changes.adds.length) await request('cart/add.js',{items:changes.adds});
    const after = Object.keys(changes.updates).length || changes.adds.length ? await state() : before;
    const pending = logic.plan(after);
    if (Object.keys(pending.updates).length || pending.adds.length) throw new Error('synchronization');
    logic.assertFree(after);
    return changes;
  }
  async function cartWithSections(sections=[]) {
    const cart = await request('cart.js');
    // The drawer is excluded on /cart; render in the current page's context.
    if (sections.length) cart.sections = await request(location.pathname + '?sections=' + sections.map(encodeURIComponent).join(','));
    return cart;
  }
  const refresh = ()=>document.dispatchEvent(new CustomEvent('cart:refresh',{bubbles:true,detail:{open:false}}));
  const api = {
    mode:'native-discount',
    add(form, event) {
      event.preventDefault();
      const button = event.submitter || form.submitButton;
      if (form.submitButton.hasAttribute('aria-disabled')) return;
      form.submitButton.setAttribute('aria-disabled','true');
      button.setAttribute('aria-busy','true');
      form.handleErrorMessage();
      let attempted = false;
      return run(async()=>{
        const fields = new FormData(form);
        if (fields.get('selling_plan')) throw new Error('configuration');
        const payload = form.prepareFormData(fields), main = payload.items.pop();
        main.quantity = Number(main.quantity || 1);
        if (!main.id || !Number.isSafeInteger(main.quantity) || main.quantity < 1) throw new Error('quantity');
        const rule = await freshRule(form.closest('product-info'));
        const before = await state();
        const waivers = logic.clampWaivers(before);
        if (JSON.stringify(waivers) !== JSON.stringify(before.giftWaivers)) await request('cart/update.js',{attributes:{drv_discount_gift_waivers:JSON.stringify(waivers)}});
        before.giftWaivers = waivers;
        const projected = {...before,items:[...before.items,{product_id:rule.productId,variant_id:main.id,quantity:main.quantity,properties:{}}],rules:[...before.rules.filter(r=>String(r.productId)!==String(rule.productId)),rule]};
        const changes = logic.plan(projected);
        const giftVariants = new Set(Array.from(logic.allowances(projected).gifts.keys()));
        if (payload.items.some(i=>giftVariants.has(String(i.id)))) throw new Error('configuration');
        const gifts = [...changes.adds];
        for (const [key,target] of Object.entries(changes.updates)) {
          const line = before.items.find(i=>i.key===key);
          if (target > line.quantity) gifts.push({id:String(line.variant_id),quantity:target-line.quantity,properties:line.properties});
        }
        attempted = true;
        await request('cart/add.js',{items:[main,...payload.items,...gifts]});
        await reconcile();
        if (button.matches('[data-drv-direct-checkout]')) { location.assign(root+'checkout'); return; }
        const sections = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections',{bubbles:true,detail:{sections}}));
        const cart = await cartWithSections(sections);
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate,{source:'product-form',cart});
        if (form.cartDrawer) form.cartDrawer.show(button);
        else location.assign(root+'cart');
      },true).catch(error=>{
        if (attempted) {
          form.handleErrorMessage();
          message(error);
          refresh();
        } else {
          form.handleErrorMessage(customerMessage(error));
          message(false);
        }
      }).finally(()=>{
        button.removeAttribute('aria-busy');
        form.submitButton.removeAttribute('aria-disabled');
      });
    },
    change(key, quantity, sections=[]) {
      return run(async()=>{
        let attempted = false;
        try {
          const target = Number(quantity);
          if (!Number.isSafeInteger(target) || target < 0) throw new Error('quantity');
          const before = await state(), line = before.items.find(i=>i.key===key);
          if (!line) { if (target===0) return cartWithSections(sections); throw new Error('quantity'); }
          const waivers = {...before.giftWaivers}, projected = {...before,giftWaivers:waivers,items:before.items.map(i=>i.key===key?{...i,quantity:target}:i)};
          if (logic.isManaged(line)) {
            const variant = String(line.variant_id), allowance = logic.allowances(before).desired.get(variant) || 0;
            const others = before.items.filter(i=>i.key!==key && String(i.variant_id)===variant).reduce((n,i)=>n+i.quantity,0);
            if (target+others > allowance) throw new Error('quantity');
            waivers[variant] = Math.max(0,allowance-target-others);
          }
          projected.giftWaivers = logic.clampWaivers(projected);
          const changes = logic.plan(projected);
          attempted = true;
          await request('cart/update.js',{updates:{...changes.updates,[key]:target},attributes:{drv_discount_gift_waivers:JSON.stringify(projected.giftWaivers)}});
          if (changes.adds.length) await request('cart/add.js',{items:changes.adds});
          await reconcile();
          return cartWithSections(sections);
        } catch (error) {
          if (attempted) {
            // A failed verification does not roll back a successful cart write.
            // Publish the actual cart while this run is still busy, so init does
            // not start another reconciliation from this recovery notification.
            try {
              const cart = await cartWithSections(sections);
              theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate,{source:'drv-gift-recovery',cart});
            } catch { refresh(); }
          }
          throw error;
        }
      }).catch(error=>{error.message=customerMessage(error);throw error;});
    },
    sync() { return run(reconcile); },
    whenIdle() { return chain; }
  };
  window.DrvNativeGifts = api;
  document.addEventListener('submit', event=>{
    const form = event.target;
    if (allowedForms.has(form)) return;
    const button = event.submitter;
    const url = new URL(form.action || '/',location.origin);
    if (url.origin !== location.origin || !url.pathname.endsWith('/cart') || (button && button.name !== 'checkout') || !form.querySelector('[name="checkout"]') && button?.name !== 'checkout') return;
    event.preventDefault();event.stopImmediatePropagation();
    if (checkoutPending) return;
    checkoutPending=true;
    run(reconcile).then(()=>{
      if (!form.isConnected) throw new Error('synchronization');
      allowedForms.add(form);
      try { form.requestSubmit(button || form.querySelector('[name="checkout"]')); }
      finally { allowedForms.delete(form); }
    }).catch(()=>{}).finally(()=>{checkoutPending=false;});
  },true);
  document.addEventListener('click', event=>{
    const retry = event.target.closest('[data-drv-discount-gift-retry]');
    if (retry) { event.preventDefault();api.sync().then(refresh).catch(()=>{});return; }
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href,location.origin);
    if (url.origin !== location.origin || !/\/checkout\/?$/.test(url.pathname)) return;
    event.preventDefault();event.stopImmediatePropagation();
    if (checkoutPending) return;
    checkoutPending=true;
    run(reconcile).then(()=>location.assign(url.href)).catch(()=>{}).finally(()=>{checkoutPending=false;});
  },true);
  const init = ()=>{
    if (busy) return;
    return api.sync().then(changes=>{if(changes.adds.length || Object.keys(changes.updates).length) refresh();}).catch(()=>{});
  };
  if (document.readyState==='loading') document.addEventListener('DOMContentLoaded',init,{once:true});
  else init();
  window.addEventListener('pageshow',event=>{if(event.persisted)init();});
  document.addEventListener('cart:refresh',init);
  theme.pubsub.subscribe?.(theme.pubsub.PUB_SUB_EVENTS.cartUpdate,init);
})();
